<template>
	<div>
		<TransitionRoot as="template" :show="showPrompt">
			<Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" :open="showPrompt">
				<!-- The backdrop, rendered as a fixed sibling to the panel container -->
				<div class="fixed inset-0 bg-black/30" aria-hidden="true" />
				<div
					class="fixed inset-0 flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
				>
					<!-- This element is to trick the browser into centering the modal contents. -->
					<span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
						>&#8203;</span
					>
					<TransitionChild
						as="template"
						enter="ease-out duration-300"
						enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enter-to="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leave-from="opacity-100 translate-y-0 sm:scale-100"
						leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<div
							class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full p-6"
						>
							<div>
								<div
									class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100"
								>
									<PencilSquareIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
								</div>
								<div class="mt-3 text-center sm:mt-5">
									<DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
										Email adres veranderen
									</DialogTitle>
									<DialogDescription as="h4" class="text-gray-700">
										{{ user.name }}<br />
										({{ user.email }})
									</DialogDescription>
									<div class="mt-2">
										<PulseLoader v-if="loading" class="mt-8 mb-8 text-center w-full"></PulseLoader>
										<div v-show="!loading && !success" class="max-w-3xl mx-auto">
											<div class="bg-white">
												<form v-if="step === 0" class="space-y-8 divide-y divide-gray-200">
													<div class="space-y-8 divide-y divide-gray-200 sm:space-y-5">
														<div class="sm:mt-5 space-y-6 sm:space-y-5">
															<div
																class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
															>
																<label
																	for="about"
																	class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
																>
																	Nieuwe email adres
																	<span class="text-red-600">*</span>
																</label>
																<div class="mt-1 sm:mt-0 sm:col-span-2">
																	<input
																		:value="email1Value"
																		type="email"
																		:class="[
																			email1Error ? 'border-red-600' : 'border-gray-300',
																			'max-w-lg shadow-xs block w-full focus:ring-green-500 focus:border-green-500 sm:text-sm border rounded-md'
																		]"
																		@change="email1Change"
																	/>
																</div>
															</div>

															<div
																class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
															>
																<label
																	for="about"
																	class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
																>
																	Bevestig email adres
																	<span class="text-red-600">*</span>
																</label>
																<div class="mt-1 sm:mt-0 sm:col-span-2">
																	<input
																		:value="email2Value"
																		type="email"
																		:class="[
																			email2Error ? 'border-red-600' : 'border-gray-300',
																			'max-w-lg shadow-xs block w-full focus:ring-green-500 focus:border-green-500 sm:text-sm border rounded-md'
																		]"
																		@change="email2Change"
																	/>
																</div>
															</div>
														</div>
													</div>
												</form>
												<form v-if="step === 1" class="divide-gray-200">
													<h2 class="mt-2 text-lg font-extrabold text-gray-900">
														Voer de code in die u heeft ontvangen
													</h2>
													<div class="mt-2">
														<div class="flex text-center">
															<input
																v-for="(codeInput, index) in codeInputs"
																:key="codeInput.ref"
																:ref="codeInput.ref"
																v-model="codeInput.value"
																type="text"
																:class="TFAState"
																class="text-center flex-1 m-2 w-9 text-lg shadow-xs focus:ring-green-500 focus:border-green-500 block rounded-md"
																@keyup="handleCodeKey($event, index)"
																@input="handleCodeInput(codeInput.value, index)"
															/>
														</div>
														<div class="mt-6 relative">
															<a
																class="relative flex justify-center text-sm"
																href="#"
																@click="twoFactorAuthenticationResend"
															>
																<span
																	class="px-2 bg-white bold text-green-500 hover:text-green-800"
																>
																	↺ {{ t('login.resend') }}
																</span>
															</a>
														</div>
													</div>
												</form>
											</div>
										</div>
										<div
											v-if="success"
											class="mx-auto mt-6 flex items-center justify-center h-12 w-12 rounded-full"
										>
											<CheckIcon class="h-8 w-8 text-green-600" aria-hidden="true" />
										</div>
										<p v-if="success">
											Windpark Zeewolde is op de hoogte van uw nieuwe email adres en zal contact met
											u opnemen om te bevestigen wanneer het is is geupdate.
										</p>
									</div>
								</div>
							</div>
							<div class="text-center text-red-600 text-sm mt-3 mx-3">
								{{ errorMsg }}
							</div>
							<div v-if="success" class="mt-5 sm:mt-6 flex">
								<button
									type="button"
									class="inline-flex justify-center flex-1 rounded-md border border-transparent shadow-xs px-4 py-2 ml-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm"
									@click="close()"
								>
									Sluit
								</button>
							</div>
							<div v-if="!loading && !success" class="mt-5 sm:mt-6 flex">
								<button
									type="button"
									class="inline-flex justify-center flex-1 rounded-md border shadow-xs px-4 py-2 mr-2 bg-white text-base font-medium hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm"
									@click="showPrompt = false"
								>
									Annuleren
								</button>
								<button
									type="button"
									class="inline-flex justify-center flex-1 rounded-md border border-transparent shadow-xs px-4 py-2 ml-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm"
									@click="save()"
								>
									Doorgaan
								</button>
							</div>
						</div>
					</TransitionChild>
				</div>
			</Dialog>
		</TransitionRoot>
		<ErrorDialog ref="errorPrompt" />
	</div>
</template>
<script lang="ts">
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'
import ErrorDialog from './ErrorDialog.ts.vue'
import {
	Dialog,
	DialogDescription,
	DialogOverlay,
	DialogTitle,
	TransitionChild,
	TransitionRoot
} from '@headlessui/vue'
import { CheckIcon, PencilSquareIcon } from '@heroicons/vue/24/outline'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import relaticsService from '@/services/relaticsService'
import helpers from '@/helpers/generalHelpers'

export default {
	components: {
		// eslint-disable-next-line vue/no-reserved-component-names
		Dialog,
		DialogOverlay,
		DialogTitle,
		DialogDescription,
		TransitionChild,
		TransitionRoot,
		PencilSquareIcon,
		ErrorDialog,
		PulseLoader,
		CheckIcon
	},
	setup() {
		const { t } = useI18n({
			inheritLocale: true,
			useScope: 'global'
		})
		return {
			t
		}
	},
	data() {
		return {
			loading: false,
			success: false,
			showPrompt: false,
			step: 0,

			email1Value: '',
			email2Value: '',

			email1Error: false,
			email2Error: false,

			errorMsg: '',

			TFAValid: true,
			codeInputs: [
				{
					value: '',
					ref: 'codeA'
				},
				{
					value: '',
					ref: 'codeB'
				},
				{
					value: '',
					ref: 'codeC'
				},
				{
					value: '',
					ref: 'codeD'
				},
				{
					value: '',
					ref: 'codeE'
				},
				{
					value: '',
					ref: 'codeF'
				}
			]
		}
	},
	computed: {
		...mapGetters({
			user: 'getCurrentUser'
		}),
		TFAState() {
			if (this.TFAValid) {
				return 'border-gray-300 border'
			}
			return 'border-red-400 border-2 '
		}
	},
	methods: {
		showDialog() {
			this.email1Value = ''
			this.email2Value = ''
			this.email1Error = false
			this.email2Error = false
			this.codeInputs = [
				{
					value: '',
					ref: 'codeA'
				},
				{
					value: '',
					ref: 'codeB'
				},
				{
					value: '',
					ref: 'codeC'
				},
				{
					value: '',
					ref: 'codeD'
				},
				{
					value: '',
					ref: 'codeE'
				},
				{
					value: '',
					ref: 'codeF'
				}
			]
			this.step = 0
			this.showPrompt = true
		},
		email1Change(e: any) {
			this.email1Value = e.target.value
		},
		email2Change(e: any) {
			this.email2Value = e.target.value
		},
		save() {
			this.email1Error = false
			this.email2Error = false

			if (!this.email1Value || !helpers.isEmail(this.email1Value)) {
				this.email1Error = true
			}
			if (!this.email2Value || this.email2Value != this.email1Value) {
				this.email2Error = true
			}

			if (!this.email1Error && !this.email2Error) {
				this.loading = true
				this.twoFactorAuthenticationResend()
			}
		},
		close() {
			this.success = false
			this.showPrompt = false
		},

		twoFactorAuthenticationResend() {
			this.loading = true
			var parent = this
			const data = relaticsService.updateEmailRequest(this.user.relaticsKey, this.email1Value)
			Promise.resolve(data).then(function (value: any) {
				parent.loading = false
				if (value.status === 200 && value && value.data) {
					parent.loading = false
					parent.step = 1
				} else {
					parent.$refs.errorPrompt.showDialog()
				}
			}, null)
		},
		handleCodeKey(e: any, codeIndex: any) {
			if (codeIndex != 0 && e.key === 'Backspace') {
				this.$refs[this.codeInputs[codeIndex - 1].ref].focus()
			}
		},
		handleCodeInput(c: any, codeIndex: any) {
			if (c) {
				for (let i = 0; i < Math.min(c.length, this.codeInputs.length); i++) {
					this.codeInputs[codeIndex + i].value = c.charAt(i)
				}
				if (
					codeIndex + c.length < this.codeInputs.length &&
					this.codeInputs.reduce((total, c) => total + c.value, '').length < 6
				) {
					this.$refs[this.codeInputs[codeIndex + c.length].ref].focus()
				} else {
					this.submitCode()
				}
			}
		},

		submitCode() {
			this.loading = true
			const data = relaticsService.updateEmail(
				this.user.relaticsKey,
				this.email1Value,
				this.codeInputs.reduce((total, c) => total + c.value, '')
			)
			var parent: any = this
			Promise.resolve(data).then(function (value: any) {
				parent.loading = false
				if (value.status === 200 && value) {
					parent.showPrompt = true
					parent.selectedCategory = null
					parent.selectedReden = ''
					parent.selectedFile = null
					parent.selectedDatum = null
					parent.success = true
				} else if (value.response.data.code === 'REL815') {
					parent.errorMsg = 'Code is onjuist'
				} else {
					parent.$refs.errorPrompt.showDialog()
				}
			}, null)
		}
	}
}
</script>
